/**
 * Utilitaires de gestion des permissions — dashboards SELLER & ADMIN.
 *
 * Les permissions sont des chaînes au format "module:action"
 * (ex: "products:create", "vendors:validate", "dashboard:read").
 *
 * Règles d'accès :
 *  - Si l'utilisateur a un `customRole` assigné, ses permissions sont celles
 *    du customRole (y compris pour les admins sous-rôles). Priorité absolue.
 *  - Sinon, un ADMIN plateforme (rôle "ADMIN" sans customRole) → accès total.
 *  - Sinon, un propriétaire de boutique sans customRole → accès total.
 *  - Sinon → aucune permission.
 *  - Wildcard supporté : "products:*" donne accès à "products:read",
 *    "products:create", etc. "*" ou "*:*" donnent accès total.
 */

// Liste ordonnée des chemins possibles pour récupérer les permissions
// d'un utilisateur en fonction de la forme de l'objet renvoyé par l'API.
// `customRole.permissions` est la source principale (backend actuel).
const PERMISSION_PATHS = [
    (u) => u?.customRole?.permissions,
    (u) => u?.permissions,
    (u) => u?.role?.permissions,
    (u) => u?.assignedRole?.permissions,
    (u) => u?.shopRole?.permissions,
    (u) => u?.roleDetails?.permissions,
    (u) => (Array.isArray(u?.roles) ? u.roles.flatMap((r) => r?.permissions || []) : null),
];

/**
 * Extrait le tableau de permissions depuis un objet utilisateur,
 * en essayant plusieurs emplacements connus.
 *
 * Particularité : si `customRole` existe mais est désactivé
 * (`isActive: false`), on retourne [] — le rôle est révoqué.
 *
 * @returns {string[]} tableau de permissions ("module:action")
 */
export const extractPermissions = (user) => {
    if (!user) return [];
    // Rôle personnalisé explicitement désactivé → aucune permission
    if (user.customRole && user.customRole.isActive === false) return [];
    for (const getter of PERMISSION_PATHS) {
        const value = getter(user);
        if (Array.isArray(value) && value.length > 0) {
            return value.filter(Boolean);
        }
    }
    return [];
};

/**
 * Détecte si l'utilisateur est le propriétaire de la boutique.
 * Dans ce cas, il a accès à tout.
 */
export const isShopOwner = (user) => {
    if (!user) return false;
    if (user.isOwner === true || user.isShopOwner === true) return true;
    if (user.shopOwnerId && user.id && String(user.shopOwnerId) === String(user.id))
        return true;
    if (user.shop?.ownerId && user.id && String(user.shop.ownerId) === String(user.id))
        return true;
    // Fallback : certaines APIs renvoient un champ "type" ou "userType"
    if (user.userType === "OWNER" || user.type === "OWNER") return true;
    /**
     * Propriétaire boutique (ex. « Devenir vendeur ») : SELLER + shopId, sans
     * roleId staff. Le staff invité a toujours un roleId (+ customRole au /me).
     * L’API devrait exposer isOwner à terme ; en attendant ce signal suffit.
     */
    if (
        user.role === "SELLER" &&
        user.shopId &&
        !user.roleId &&
        !user.customRole
    ) {
        return true;
    }
    return false;
};

/**
 * Détecte un admin plateforme (accès total).
 */
export const isPlatformAdmin = (user) => user?.role === "ADMIN";

/**
 * Vrai si l'utilisateur a un accès total.
 *
 * Règle clé : si un `customRole` est explicitement assigné à l'utilisateur,
 * on NE court-circuite PAS — ses permissions sont celles du rôle (même pour
 * les admins plateforme à qui on aurait attribué un rôle restreint).
 * L'accès total est réservé aux users ADMIN/owner SANS customRole.
 */
export const hasFullAccess = (user) => {
    if (!user) return false;
    // Un customRole assigné = on respecte strictement ses permissions
    if (user.customRole) return false;
    if (isPlatformAdmin(user) || isShopOwner(user)) return true;
    return false;
};

/**
 * Vérifie si une permission spécifique est contenue dans un ensemble
 * de permissions, en supportant les wildcards.
 */
const matchesPermission = (userPerms, required) => {
    if (!required) return true;
    if (userPerms.includes("*") || userPerms.includes("*:*")) return true;
    if (userPerms.includes(required)) return true;

    const [module, action] = String(required).split(":");
    if (userPerms.includes(`${module}:*`)) return true;
    if (action && userPerms.includes(`*:${action}`)) return true;
    return false;
};

/**
 * L'utilisateur a-t-il la permission demandée ?
 * @param {object} user - objet utilisateur
 * @param {string|string[]} required - une permission ou un tableau (ET logique)
 */
export const hasPermission = (user, required) => {
    if (!required || (Array.isArray(required) && required.length === 0)) return true;
    if (!user) return false;
    if (hasFullAccess(user)) return true;

    const perms = extractPermissions(user);
    if (perms.length === 0) return false;

    if (Array.isArray(required)) {
        return required.every((r) => matchesPermission(perms, r));
    }
    return matchesPermission(perms, required);
};

/**
 * L'utilisateur a-t-il au moins une des permissions listées ?
 */
export const hasAnyPermission = (user, requiredList = []) => {
    if (!requiredList || requiredList.length === 0) return true;
    if (!user) return false;
    if (hasFullAccess(user)) return true;

    const perms = extractPermissions(user);
    return requiredList.some((r) => matchesPermission(perms, r));
};

/**
 * Mapping des chemins de routes → permission requise.
 * Utilisé par la sidebar et par les gardes de routes.
 */
export const ROUTE_PERMISSIONS = {
    // --- SELLER ---
    "/dashboard": "dashboard:read",
    "/dashboard/orders": "orders:read",
    "/dashboard/revenues": "revenues:read",
    "/dashboard/products": "products:read",
    "/dashboard/products/add": "products:create",
    "/dashboard/reviews": "reviews:read",
    "/dashboard/promos": "promos:read",
    "/dashboard/access-roles": "access-roles:read",
    "/dashboard/settings": "settings:read",
    // --- ADMIN ---
    "/admin-dashboard": "dashboard:read",
    "/admin-dashboard/vendors": "vendors:read",
    "/admin-dashboard/users": "clients:read",
    "/admin-dashboard/products": "products-validation:read",
    "/admin-dashboard/reviews": "reviews-moderation:read",
    "/admin-dashboard/categories": "categories:read",
    "/admin-dashboard/documents-generale": "documents-generale:read",
    "/admin-dashboard/all-orders": "all-orders:read",
    "/admin-dashboard/cms/slider": "cms-slider:read",
    "/admin-dashboard/cms/banner": "cms-banner:read",
    "/admin-dashboard/finances/payments": "payments:read",
    "/admin-dashboard/finances/commissions": "commissions:read",
    "/admin-dashboard/faq-messages": "faq-messages:read",
    "/admin-dashboard/access-roles": "access-roles:read",
    "/admin-dashboard/stock-maintenance": "settings:read",
    "/admin-dashboard/settings": "settings:read",
};

/**
 * Renvoie la première route accessible à l'utilisateur.
 * Prend en compte le contexte (admin vs seller) pour rediriger vers
 * la bonne section de l'application.
 */
export const getFirstAccessibleRoute = (user, fallback = "/unauthorized") => {
    const isAdmin = user?.role === "ADMIN";
    const candidates = isAdmin
        ? [
              "/admin-dashboard",
              "/admin-dashboard/vendors",
              "/admin-dashboard/users",
              "/admin-dashboard/products",
              "/admin-dashboard/reviews",
              "/admin-dashboard/categories",
              "/admin-dashboard/all-orders",
              "/admin-dashboard/cms/slider",
              "/admin-dashboard/cms/banner",
              // TODO(rappel): réactiver après retour de /admin-dashboard/finances/payments
              // "/admin-dashboard/finances/payments",
              "/admin-dashboard/finances/commissions",
              "/admin-dashboard/faq-messages",
              "/admin-dashboard/documents-generale",
              "/admin-dashboard/access-roles",
              "/admin-dashboard/stock-maintenance",
              "/admin-dashboard/settings",
          ]
        : [
              "/dashboard",
              "/dashboard/orders",
              "/dashboard/products",
              "/dashboard/reviews",
              "/dashboard/access-roles",
              "/dashboard/settings",
          ];
    for (const route of candidates) {
        const perm = ROUTE_PERMISSIONS[route];
        if (!perm || hasPermission(user, perm)) return route;
    }
    return fallback;
};
